import React from 'react';

import { useTranslation } from 'react-i18next';

import { Element } from '@craftjs/core';

import { Box, Divider, Typography } from '@mui/material';

import Container from '@resolvers/pdf/Container/Container';
import Table from '@resolvers/common/Table/Table';
import Button from '@resolvers/common/Button/Button';
import Placeholder from '@resolvers/common/Placeholder/Placeholder';
import { CanvasImage } from '@resolvers/common/Images/CraftImage';
import Cards from '@resolvers/pdf/Cards/Cards';
import PdfParagraph from '@resolvers/email/Paragraph/PdfParagraph';
import EmailParagraph from '@resolvers/pdf/Paragraph/EmailParagraph';
import EmailContainer from '@resolvers/email/Container/EmailContainer';
import EmailContainerColumn from '@resolvers/email/Container/EmailContainerColumn';
import EmailContainerRow from '@resolvers/email/Container/EmailContainerRow';

import MyCheckbox from '@common/components/MyCheckbox/MyCheckbox';
import { useBorder } from '@common/providers/BorderProvider/BorderProvider';

import UserPrev from './UserPrev';
import TableHeader from '@resolvers/common/Table/components/TableHeader';
import TableRow from '@resolvers/common/Table/components/TableRow';
import TableFooter from '@resolvers/common/Table/components/TableFooter';
import TableCell from '@resolvers/common/Table/components/TableCell';
import {
  selectBackgroundColor,
  selectTypographyColor,
} from '@common/providers/ThemeProvider/selectors';
import { isTemplateTypeEmail, isTemplatePdf } from '@common/helpers/helpers';

const Toolbox = ({ templateType }) => {
  const { t } = useTranslation();
  const { showBorders, setShowBorders } = useBorder();

  return (
    <Box sx={{ backgroundColor: selectBackgroundColor }}>
      <Typography
        sx={{
          p: 1,
          fontSize: '14px',
          color: selectTypographyColor,
        }}
      >
        {t('droppable_components')}
      </Typography>
      <Box
        sx={{ display: 'flex', p: 1, mb: 2, flexWrap: 'wrap', rowGap: '3px' }}
      >
        {isTemplatePdf(templateType) && (
          <>
            <UserPrev
              name={t('container')}
              element={<Element canvas is={Container} />}
              iconName='containerGrid'
            />
            <UserPrev
              name={t('cards')}
              element={<Element canvas is={Cards} />}
              iconName='cards'
            />
            <UserPrev
              name={t('paragraph')}
              element={<Element canvas is={PdfParagraph} />}
              iconName='textA'
            />
            <UserPrev
              name={t('placeholder')}
              element={<Element canvas is={Placeholder} />}
              iconName='puzzle'
            />
            <UserPrev
              name={t('table')}
              element={
                <Element canvas is={Table}>
                  <Element canvas is={TableHeader}>
                    <Element
                      canvas
                      is={TableCell}
                      elementProps={{ isHeader: true }}
                    />
                  </Element>
                  <Element canvas is={TableRow}>
                    <Element canvas is={TableCell} />
                  </Element>
                  <Element canvas is={TableFooter}>
                    <Element
                      canvas
                      is={TableCell}
                      elementProps={{ isFooter: true }}
                    />
                  </Element>
                </Element>
              }
              iconName='table'
            />
            <UserPrev
              name={t('fixedTable')}
              element={
                <Element canvas is={Table} elementProps={{ isFixed: true }} />
              }
              iconName='fixedTable'
            />
          </>
        )}
        {isTemplateTypeEmail(templateType) && (
          <>
            <UserPrev
              name={t('container')}
              element={<Element canvas is={EmailContainer} />}
              iconName='containerGrid'
            />
            <UserPrev
              name={t('container_row')}
              element={<Element canvas is={EmailContainerRow} />}
              iconName='containerRow'
            />
            <UserPrev
              name={t('container_column')}
              element={<Element canvas is={EmailContainerColumn} />}
              iconName='containerColumn'
            />
            <UserPrev
              name={t('paragraph')}
              element={<Element canvas is={EmailParagraph} />}
              iconName='textA'
            />

            <UserPrev
              name={t('placeholder')}
              element={<Element canvas is={Placeholder} />}
              iconName='puzzle'
            />
            <UserPrev
              name={t('table')}
              element={
                <Element canvas is={Table}>
                  <Element canvas is={TableHeader}>
                    <Element
                      canvas
                      is={TableCell}
                      elementProps={{ isHeader: true }}
                    />
                  </Element>
                  <Element canvas is={TableRow}>
                    <Element canvas is={TableCell} />
                  </Element>
                  <Element canvas is={TableFooter}>
                    <Element
                      canvas
                      is={TableCell}
                      elementProps={{ isFooter: true }}
                    />
                  </Element>
                </Element>
              }
              iconName='table'
            />
            <UserPrev
              name={t('fixedTable')}
              element={
                <Element canvas is={Table} elementProps={{ isFixed: true }} />
              }
              iconName='fixedTable'
            />
            <UserPrev
              name={t('button')}
              element={<Element canvas is={Button} />}
              iconName='pointer'
            />
            <UserPrev
              name={t('imageWithUrl')}
              element={<Element canvas is={CanvasImage} />}
              iconName='imageIcon'
            />
          </>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 1 }}>
        <MyCheckbox
          sx={{ color: selectTypographyColor }}
          checked={showBorders}
          onChange={() => setShowBorders(!showBorders)}
          label={
            <Typography
              sx={{
                fontSize: '14px',
                color: selectTypographyColor,
              }}
            >
              {t('show_grid_components')}
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};

export default Toolbox;
